import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';

import { environment } from '@orderna/admin-panel/src/environments/environment';
import { IPurchasesApiService } from './purchases-api.interface';
import { SuccessResponse } from '../shared/success-response';
import { FindPurchasesQueryDto } from './dtos/queries/find-purchases.query.dto';
import { FindPurchasesResponseDto } from './dtos/queries/find-purchases.response.dto';
import { FindPurchaseResponseDto } from './dtos/queries/find-purchase.response.dto';
import { ExportPurchasesQueryDto } from './dtos/queries/export-purchases.query.dto';
import { HandleBlobHttpResponseOptions } from '@orderna/admin-panel/src/types/handle-blob-http-response-options';
import { handleBlobHttpResponse } from '@orderna/admin-panel/src/utils/handle-blob-http-response';
import { ExportExcelPdfService } from '../export-excel-pdf/export-excel-pdf.service';
import { CreatePurchaseRequestDto } from './dtos/commands/create-purchase.request.dto';
import { CreatePurchaseResponseDto } from './dtos/commands/create-purchase.response.dto';
import { UpdatePurchaseRequestDto } from './dtos/commands/update-purchase.request.dto';
import { UpdatePurchaseResponseDto } from './dtos/commands/update-purchase.response.dto';

@Injectable({
  providedIn: 'root',
})
export class PurchasesApiService implements IPurchasesApiService {
  private http = inject(HttpClient);
  private exportService = inject(ExportExcelPdfService);
  private API_URL = `${environment.api}/purchases`;

  async createPurchase(
    data: CreatePurchaseRequestDto
  ): Promise<CreatePurchaseResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<CreatePurchaseResponseDto>>(
        this.API_URL,
        data
      )
    );

    return response.data;
  }

  async updatePurchase(
    purchaseId: number,
    data: UpdatePurchaseRequestDto
  ): Promise<UpdatePurchaseResponseDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<UpdatePurchaseResponseDto>>(
        `${this.API_URL}/${purchaseId}`,
        data
      )
    );

    return response.data;
  }

  deletePurchase(purchaseId: number): Observable<any> {
    return this.http.delete(`${this.API_URL}/${purchaseId}`);
  }

  deletePurchases(purchaseIds: number[]): Observable<any> {
    return this.http.post(`${this.API_URL}/delete-many`, {
      ids: purchaseIds,
    });
  }

  async findPurchases(
    query: FindPurchasesQueryDto
  ): Promise<FindPurchasesResponseDto> {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindPurchasesResponseDto>>(this.API_URL, {
        params,
      })
    );

    return response.data;
  }

  async exportPurchases(
    query: ExportPurchasesQueryDto,
    options?: HandleBlobHttpResponseOptions
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get(`${this.API_URL}/export`, {
        responseType: 'blob',
        observe: 'response',
        params,
      })
    );

    handleBlobHttpResponse(response, options, this.exportService);
  }

  async findPurchase(purchaseId: number) {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindPurchaseResponseDto>>(
        `${this.API_URL}/${purchaseId}`
      )
    );

    return response.data;
  }
}
